import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type MoyensConnexion } from './api';

/**
 * CONNEXION (§44) — l'identité vient de Mowibo ID, jamais d'ici.
 *
 * ⚠️ CET ÉCRAN NE RÉCITE PLUS SON ÉTAT, IL LE DEMANDE. La version précédente
 * portait en dur : « le client OIDC cadastral n'est pas déclaré dans Mowibo ID
 * (mesuré : HTTP 400) ». C'était vrai le 2026-09-08. Le jour de la déclaration,
 * cette phrase devient fausse **sans qu'aucun test ne rougisse** — les deux
 * formulations sont du texte valide. C'est le motif « l'étiquette décrit
 * l'intention, pas la réalité », déjà payé sur l'avertissement des mutations.
 *
 * `GET /session/moyens` va REGARDER : le service interroge Mowibo ID et
 * distingue 302 (client déclaré) de 400 (inconnu). L'écran affiche ce qu'on lui
 * répond, y compris quand la réponse ne l'arrange pas.
 *
 * ⚠️ ET LE CODE D'INVITATION SE SAISIT AVANT DE PARTIR. Au retour de Mowibo ID,
 * une personne sans compte cadastral serait refusée en 403 ; le code doit donc
 * accompagner l'échange. On le range dans `sessionStorage` — pas le jeton, le
 * CODE, qui ne vaut rien sans une authentification réussie et qui expire avec
 * l'invitation.
 */

const CLE_INVITATION = 'cadastral.invitation';

export function Connexion({ onOuverte }: { onOuverte: () => void }) {
  const [jeton, setJeton] = useState('');
  const [invitation, setInvitation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [aide, setAide] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [moyens, setMoyens] = useState<MoyensConnexion | null>(null);
  const [retourEnCours, setRetourEnCours] = useState(false);

  useEffect(() => { api.session.moyens().then(setMoyens).catch(() => setMoyens(null)); }, []);

  const messageDErreur = useCallback((err: unknown): string => {
    if (!(err instanceof ErreurApi)) return err instanceof Error ? err.message : String(err);
    // Les causes ne se corrigent pas de la même façon : le dire, chacune.
    if (err.estIndisponibilite) return "Mowibo ID est injoignable. Ce n'est pas vous : le service d'identité ne répond pas.";
    if (err.statut === 401) return err.message || "Jeton refusé par Mowibo ID (signature, émetteur ou expiration).";
    if (err.statut === 403) return err.message;
    return err.message;
  }, []);

  /**
   * Retour d'autorisation. ⚠️ L'URL EST NETTOYÉE DANS TOUS LES CAS, y compris
   * en cas d'échec : un `code` qui reste dans la barre d'adresse est rejoué au
   * moindre rafraîchissement, et Mowibo ID répond alors « code déjà utilisé » —
   * un message qui envoie chercher le défaut très loin de sa cause.
   */
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const code = p.get('code'); const etat = p.get('state'); const refus = p.get('error');
    if (!code && !refus) return;

    const nettoyer = () => window.history.replaceState({}, '', window.location.pathname);

    if (refus) {
      nettoyer();
      setErreur(refus === 'access_denied'
        ? "Vous avez refusé de partager votre identité. Aucune session n'a été ouverte."
        : `Mowibo ID a interrompu la connexion (${refus}).`);
      return;
    }
    setRetourEnCours(true);
    const codeInvitation = sessionStorage.getItem(CLE_INVITATION) ?? undefined;
    api.session.oidc.retour(code!, etat ?? '', codeInvitation)
      .then(() => { sessionStorage.removeItem(CLE_INVITATION); nettoyer(); onOuverte(); })
      .catch((err) => {
        nettoyer();
        setErreur(messageDErreur(err));
        if (err instanceof ErreurApi && (err.details as any)?.enrolement_possible) {
          setAide("Votre identité Mowibo ID est reconnue, mais aucun compte cadastral ne lui est rattaché. "
            + "Si un administrateur vous a remis un code d'invitation, saisissez-le ci-dessous puis relancez la connexion.");
        }
      })
      .finally(() => setRetourEnCours(false));
  }, [onOuverte, messageDErreur]);

  async function partirVersMowiboId() {
    setErreur(null); setEnCours(true);
    try {
      if (invitation.trim()) sessionStorage.setItem(CLE_INVITATION, invitation.trim());
      else sessionStorage.removeItem(CLE_INVITATION);
      const { url } = await api.session.oidc.debut();
      window.location.assign(url);
    } catch (err) {
      setErreur(messageDErreur(err)); setEnCours(false);
    }
  }

  async function ouvrirParJeton(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setAide(null); setEnCours(true);
    try {
      await api.session.ouvrir(jeton.trim(), invitation.trim() || undefined);
      onOuverte();
    } catch (err) {
      setErreur(messageDErreur(err));
      if (err instanceof ErreurApi && (err.details as any)?.enrolement_possible) {
        setAide("Jeton valide, mais aucun compte cadastral n'y est rattaché. "
          + "Saisissez votre code d'invitation ci-dessus, ou demandez une habilitation.");
      }
    } finally { setEnCours(false); }
  }

  if (retourEnCours) {
    return <div className="attente-plein">Retour de Mowibo ID — ouverture de la session…</div>;
  }

  const oidcPret = moyens?.oidc.disponible === true;

  return (
    <div className="connexion-page">
      <div className="connexion-carte">
        <div className="marque">
          <span className="sigle">MC</span>
          <div>
            <h1>Mowibo Cadastral</h1>
            <p>Registre foncier et intelligence territoriale</p>
          </div>
        </div>

        <button
          type="button" className="bouton primaire"
          disabled={!oidcPret || enCours}
          onClick={partirVersMowiboId}
          title={oidcPret ? 'Authentification par Mowibo ID' : moyens?.oidc.detail ?? 'état inconnu'}
        >
          {enCours && oidcPret ? 'Redirection…' : 'Se connecter avec Mowibo ID'}
        </button>

        {moyens === null && <p className="note-indispo">Interrogation du service d'identité…</p>}
        {moyens !== null && !oidcPret && (
          <p className="note-indispo">
            Indisponible : {moyens.oidc.detail}.
            {' '}Utilisez le jeton de service ci-dessous en attendant.
          </p>
        )}
        {oidcPret && (
          <p className="mention">
            Vous serez redirigé vers <code>id.mowibo.com</code>, qui vous demandera votre
            accord avant de transmettre quoi que ce soit. Session de {moyens?.sessionDureeH ?? 12} heures.
          </p>
        )}

        <label htmlFor="invitation">
          Code d'invitation <span className="aide">première connexion seulement</span>
        </label>
        <input
          id="invitation" value={invitation} onChange={(e) => setInvitation(e.target.value)}
          placeholder="XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX"
          spellCheck={false} autoComplete="off"
        />

        <div className="separateur"><span>ou</span></div>

        <form onSubmit={ouvrirParJeton}>
          <label htmlFor="jeton">Jeton de service Mowibo ID</label>
          <textarea
            id="jeton" value={jeton} onChange={(e) => setJeton(e.target.value)}
            placeholder="eyJhbGciOiJFZERTQSIs…" rows={4} spellCheck={false} autoComplete="off"
          />
          <button className="bouton primaire" disabled={enCours || jeton.trim().length < 20}>
            {enCours ? 'Vérification…' : 'Ouvrir la session'}
          </button>
        </form>

        {erreur && <p className="erreur" role="alert">{erreur}</p>}
        {aide && <p className="note-indispo" role="status">{aide}</p>}

        <p className="mention">
          Le jeton est vérifié par Mowibo ID, puis <strong>abandonné</strong> : la session
          repose sur un cookie <code>httpOnly</code> propre au cadastre, qu'aucun script de
          cette page ne peut relire.
        </p>
      </div>
    </div>
  );
}
